<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import CardsImage from '@/assets/images/challenge-cards.png'
import TrophyImage from '@/assets/images/gifs/trophy.gif'
import CrossIcon from '@/components/icons/CrossIcon.vue'
import { T } from '@tolgee/vue'
import BaseButton from '@/components/base/BaseButton.vue'
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue'
import TabButton from '@/components/TabButton.vue'
import { computed, nextTick, ref, watch } from 'vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { formatDuration } from '@/helpers/date'
import { twMerge } from 'tailwind-merge'
import { type CardChallengeLeaderboardPlayer, CardChallengeType, ChallengeMedal } from '@/types/leaderboard-player'
import { useAuth } from '@/composables/auth'
import { useCardChallengeLeaderboard } from '@/composables/game/leader-board'
import ChallengeBoardCard from '@/components/ChallengeBoardCard.vue'
import router from '@/router'
import { RouteName } from '@/routes'
import StarsImage from '@/assets/images/star-group.png'

const props = defineProps<{
	show: boolean,
}>()

const emit = defineEmits(['hide'])

const { addHapticFeedback } = useHapticFeedback()
const { profile } = useAuth()
const {
  getCardChallengeLeaderboard,
  cardChallengeLeaderboard: players,
  currentPlayer,
  remainingTimeFromDay,
  remainingTimeFromWeek,
} = useCardChallengeLeaderboard()

const loading = ref(false)

const container = ref<HTMLElement | null>(null)

const activeTab = ref<CardChallengeType>(CardChallengeType.DAILY)

const inScrollView = ref(false)
const isScrolled = ref(false)
const maxVisibleRank = ref<number>(100)
const listLimit = ref<number>(100)

const playerForCard = computed<CardChallengeLeaderboardPlayer | undefined>(() => {
  if (currentPlayer.value) {
    return {
      firstName: profile.value.firstName,
      photoUrl: profile.value.photoUrl,
      score: currentPlayer.value.score,
      isThisPlayer: true,
    }
  }
  return undefined

})

function handleScroll() {
  if (container.value) {
    if (currentPlayer.value?.rank) {
      const cardElement = container.value.querySelector(`.card:nth-child(${currentPlayer.value.rank})`) as HTMLElement
      if (cardElement) {
        const cardRect = cardElement.getBoundingClientRect()
        inScrollView.value = cardRect.bottom <= container.value.getBoundingClientRect().bottom
      } else {
        inScrollView.value = false
      }
    }
    isScrolled.value = container.value.scrollTop > 30
  }
}

function getCardMedal(rank: number): ChallengeMedal | undefined {
  if (rank > players.value.length) return undefined
  const diff = rank - 1 > 0 ? players.value[rank - 2].score! - players.value[rank - 1].score! : 0
  switch (rank) {
  case 1: {
    return ChallengeMedal.GOLD
  }
  case 2: {
    return diff ? ChallengeMedal.SILVER : getCardMedal(rank - 1)
  }
  case 3: {
    return diff ? ChallengeMedal.BRONZE : getCardMedal(rank - 1)

  }
  default: {
    return diff ? undefined : getCardMedal(rank - 1)
  }
  }
}

function onCtaClick() {
  router.push(RouteName.MINE)
  emit('hide')
}

watch(() => [activeTab.value, props.show], async () => {
  if (props.show) {
    loading.value = true
    await getCardChallengeLeaderboard(activeTab.value)
    loading.value = false
  }
}, { immediate: true })

watch([container, currentPlayer, players], () => {
  inScrollView.value = true
  nextTick(() => {
    setTimeout(() => {
      handleScroll()
    }, 50)
  })
}, { immediate: true })

</script>
<template>
  <TheBottomSheet
    :show="show"
    centered
    base-modal-class="green-shadow overflow-visible rounded-[32px] h-[74%] max-h-[30%] mt-10"
    content-class="blue-gradient-background overflow-visible pb-0 pt-[1px]"
    outer-content-class="overflow-visible"
    border-container-class="overflow-visible"

    @hide="$emit('hide')"
  >
    <template #closeButton>
      <div
        class="absolute w-6 h-6 text-black top-0 right-0 z-[5] bg-[#FF3737] rounded-full flex items-center justify-center"
        @click="$emit('hide')"
      >
        <CrossIcon class="text-white [&>path]:stroke-[3px] size-[14px]" />
      </div>
    </template>
    <template #baseContent>
      <div
        class="h-0 w-full relative"
        @click="$emit('hide')"
      >
        <img
          class="h-auto w-full absolute top-full left-1/2 -translate-x-1/2 -translate-y-[60%]"
          :src="CardsImage"
        >
        <img
          :src="TrophyImage"
          class="size-[144px] absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[60px] z-[101] transition-all duration-300"
          :class="isScrolled ? 'scale-[70%]' : 'scale-1'"
        >
        <img
          :src="StarsImage"
          class="h-[138px] w-auto absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[78px] z-[101]"
        >
      </div>
    </template>
    <div
      class="w-[calc(100%-4px)] h-[190px] gradient-green-background fixed left-0.5 z-[4] top-0 mt-0.5 rounded-b-[32px] rotate-180"
    />
    <div
      ref="container"
      @scroll="handleScroll"
      class="h-full w-full overflow-auto scrollable-container flex flex-col flex-1 relative"
    >
      <div
        class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[154px] bg-[rgba(231,46,109,0.44)] blur-[36px] rounded-full z-[100] transition-opacity duration-300"
        :class="isScrolled ? 'opacity-0' : 'opacity-100'"
      />

      <div class="flex flex-col w-full items-center text-center pt-[110px]">
        <h2 class=" text-light-white text-4xl leading-9 font-bold mb-2">
          <T
            key-name="challenge.modal.title"
            default-value="Cards challenge"
          />
        </h2>
        <p class="text-light-white/70 font-medium text-base leading-5 -tracking-[0.32px] mb-6 whitespace-pre-line">
          <T
            key-name="challenge.modal.description"
            :default-value="`Be the ultimate card master!\nUpgrade the most cards and claim your spot at the top to win exclusive badges.`"
          />
        </p>
        <BaseButton
          class="mb-6 h-full"
          button-class="border-0 h-full py-2 pr-3"
          @click="onCtaClick"
        >
          <div class="flex items-center gap-x-2">
            <T
              key-name="challenge.modal.cta"
              default-value="UPGRADE NOW"
            />
            <ArrowRightIcon class="size-6 [&>path]:stroke-2" />
          </div>
        </BaseButton>
        <div
          class="flex w-full rounded-xl p-0.5 bg-dark-top-layer mb-2"
        >
          <TabButton
            :is-active="activeTab === CardChallengeType.DAILY"
            @click="addHapticFeedback(); activeTab = CardChallengeType.DAILY"
          >
            <T
              key-name="challenge.modal.tabs.day"
              default-value="Daily"
            />
          </TabButton>
          <TabButton
            :is-active="activeTab === CardChallengeType.WEEKLY"
            @click="addHapticFeedback(); activeTab = CardChallengeType.WEEKLY"
          >
            <T
              key-name="challenge.modal.tabs.week"
              default-value="Weekly"
            />
          </TabButton>
          <TabButton
            :is-active="activeTab === CardChallengeType.ALL_TIME"
            @click="addHapticFeedback(); activeTab = CardChallengeType.ALL_TIME"
          >
            <T
              key-name="challenge.modal.tabs.all"
              default-value="All-time"
            />
          </TabButton>
        </div>
        <div
          v-if="activeTab !== CardChallengeType.ALL_TIME"
          class="flex w-full justify-end p-x mb-2"
        >
          <span class="text-green-primary text-sm font-bold">
            <T
              key-name="challenge.modal.reset_in"
              default-value="Resets in"
            />
            {{ formatDuration(activeTab === CardChallengeType.DAILY ? remainingTimeFromDay : remainingTimeFromWeek) }}
          </span>
        </div>
        <div
          v-if="!loading"
          :class="twMerge('w-full h-full flex flex-col relative', !players.length && 'flex-1')"
        >
          <div
            v-if="players.length"
            class="flex flex-col w-full gap-y-3 pb-4 items-center"
          >
            <ChallengeBoardCard
              v-for="(leadingBoardPlayer, index) in players.slice(0, maxVisibleRank)"
              :key="index"
              :player="leadingBoardPlayer"
              :index="(index+1).toString()"
              :is-own="leadingBoardPlayer.isThisPlayer"
              :medal="getCardMedal(index+1)"
              class="card"
            />
            <p
              v-if="listLimit <= players.length"
              class="mt-3 mb-4 text-center max-w-[314px] text-base font-medium text-white/60"
              :class="currentPlayer && currentPlayer.rank > players.length && 'pb-10'"
            >
              <T
                key-name="levels.many_more"
                default-value="And many more players who didn’t make it into the top {limit}... yet."
                :params="{limit: Math.min(players.length, listLimit)}"
              />
            </p>
            <ChallengeBoardCard
              v-else
              :player="playerForCard"
              :index="`${(maxVisibleRank).toString()}+`"
              card-class="invisible"
            />
          </div>
          <div
            v-else
            class="flex flex-1 flex-col items-center justify-center gap-y-3 w-full text-center py-5"
          >
            <h2 class="text-lg -tracking-[0.36px] font-bold text-light-white">
              <T
                key-name="card-challenge.empty.title"
                default-value="Nothing to see here yet"
              />
            </h2>
            <p class="text-sm font-medium tracking-tight text-white">
              <T
                key-name="card-challenge.empty.description"
                default-value="Upgrade cards and be the first to join the challenge"
              />
            </p>
          </div>
          <div
            v-if="playerForCard && currentPlayer && !inScrollView"
            class="w-full fixed bottom-4 z-[900] left-1/2 -translate-x-1/2 px-4"
          >
            <ChallengeBoardCard
              :player="playerForCard"
              :index="currentPlayer.rank.toString()"
              :medal="getCardMedal(currentPlayer.rank)"
              is-own
              card-class="border-green-primary"
            />
          </div>
        </div>
      </div>
    </div>
  </TheBottomSheet>
</template>
<style>
.green-shadow {
	box-shadow: 0px 0px 32px 0px #3B9A7C
}

.blue-gradient-background {
	background: linear-gradient(180deg, #8DC077 0.01%, #588BA2 16.5%, #000 42.5%);
}

.scrollable-container {
	-ms-overflow-style: none; /* Internet Explorer 10+ */
	scrollbar-width: none; /* Firefox */
}

.scrollable-container::-webkit-scrollbar {
	display: none; /* Safari and Chrome */
}

.gradient-green-background {
	background: linear-gradient(rgba(70, 107, 120, 0.00) 42.47%, #679A96 65.15%, #8EB979 100%);
}

</style>